import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import {
  CartAddItemEvent,
  CartApplyPromoCodeEvent,
  CartApplySubscriptionEvent,
  CartChangeQuantityEvent,
  CartEditItemEvent,
  CartRemoveItemEvent,
  CartRemovePromoCodeEvent,
  InsuranceEvent,
  InsuranceEventType,
  LoginEvent,
  LogoutEvent,
  OrderCompleteEvent,
  RequestProductQuoteEvent,
  SessionErrorEvent,
  ServiceStatusEvent,
  AddSyncInsuranceListenerEvent,
  RemoveSyncInsuranceListenerEvent,
  RemoveInsuranceBenefitsEvent,
  AddInsuranceBenefitsEvent,
  RequestBenefitPlanEvent,
  RequestInsuranceDataEvent,
} from '@typesApp/AppEvent'
import { isSessionExpiredError, isSessionTransitionedError } from '@utils/errorUtil'
import { clearLocalFormState } from '@pages/InsuranceMember/components/FindMember/utils/localFormState'
import {
  connectorLogoutInProgress,
  connectorOrderComplete,
  sessionExpired,
  resetConnectorInitialState,
  connectorOnLogin,
} from '@redux/slices/connectorSlice'
import { resetCtaInitialState } from '@redux/slices/ctaSlice'
import { resetWorkflowInitialState } from '@redux/slices/workflowSlice'
import { resetFormsInitialState } from '@redux/slices/formsSlice'
import { resetClaimStatusInitialState } from '@redux/slices/claimStatusSlice'
import { resetPrescriptionFileInitialState } from '@redux/slices/prescriptionFileSlice'
import { overviewResetInitialState } from '@redux/slices/overviewWidgetSlice'
import {
  addSyncInsuranceListener,
  removeSyncInsuranceListener,
  resetInsuranceMemberState,
} from '@redux/slices/insuranceMemberSlice'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { sessionsApi } from '@redux/queries/sessions'
import { configApi } from '@redux/queries/config'
import { insurersApi } from '@redux/queries/insurers'
import { ApiCache } from '@redux/queries/apiCache'
import { useActiveData } from '@hooks/useActiveData'
import { useProductQuoteService } from './useProductQuoteService'
import { useInsuranceBenefitsUsage } from './useInsuranceBenefitsUsage'
import { useServiceStatus } from './useServiceStatus'
import { useBenefitPlanService } from './useBenefitPlanService'
import { useInsuranceDataService } from './useInsuranceDataService'

const DEPRECATED_EVENTS = [
  'onCartAddItem',
  'onCartRemoveItem',
  'onCartEditItem',
  'onCartChangeQuantity',
  'onCartApplySubscription',
  'onCartApplyPromoCode',
  'onCartRemovePromoCode',
]

/**
 * Handle custom events from commerce app
 */
export const useAppEventListener = (): EventListenerOrEventListenerObject => {
  const dispatch = useDispatch()
  const serviceStatus = useServiceStatus()
  const { fetchActiveData } = useActiveData()
  const { getBenefitPlan } = useBenefitPlanService()
  const { requestQuote } = useProductQuoteService()
  const { getInsuranceData } = useInsuranceDataService()
  const { addInsuranceBenefits, removeInsuranceBenefits } = useInsuranceBenefitsUsage()

  const eventHandlers = useMemo(() => {
    const resetApiState = () => {
      dispatch(sessionsApi.util.resetApiState())
      dispatch(claimRecordsApi.util.resetApiState())
      dispatch(configApi.util.resetApiState())
      dispatch(insurersApi.util.resetApiState())
    }

    const resetSliceState = (args?: { isLogout: boolean }) => {
      dispatch(resetCtaInitialState())
      dispatch(resetFormsInitialState())
      dispatch(resetWorkflowInitialState())
      dispatch(resetInsuranceMemberState())
      dispatch(resetClaimStatusInitialState())
      dispatch(resetPrescriptionFileInitialState())
      dispatch(overviewResetInitialState())

      if (args?.isLogout) {
        dispatch(connectorLogoutInProgress())
      } else {
        dispatch(resetConnectorInitialState())
      }
    }

    const resetStateOnSessionExpiry = () => {
      dispatch(sessionsApi.util.resetApiState())
      dispatch(claimRecordsApi.util.resetApiState())
      dispatch(resetInsuranceMemberState())
      dispatch(resetClaimStatusInitialState())
      dispatch(sessionExpired())
    }

    const resetStateOnCommerceSessionExpiry = () => {
      resetApiState()
      resetSliceState()
      dispatch(sessionExpired())
      ApiCache.cleanupCache()
      clearLocalFormState()
    }

    const handleLogin = (_event: LoginEvent) => {
      resetApiState()
      resetSliceState()
      dispatch(connectorOnLogin())
    }

    const resetStateOnSessionTransition = () => {
      // need to reset claim record related state
      dispatch(claimRecordsApi.util.resetApiState())
      dispatch(resetWorkflowInitialState())
      dispatch(resetInsuranceMemberState())
      dispatch(resetClaimStatusInitialState())
    }

    const handleSessionError = (event: SessionErrorEvent) => {
      const error = event?.payload
      if (isSessionExpiredError(error)) {
        if (error?.isInternal) {
          resetStateOnSessionExpiry()
        } else {
          resetStateOnCommerceSessionExpiry()
        }
      }

      if (isSessionTransitionedError(error)) {
        resetStateOnSessionTransition()
      }
    }

    const handleOrderComplete = (_event: OrderCompleteEvent) => {
      // verify order has been placed before reset
      fetchActiveData()
        .then(result => {
          if (result?.id) {
            console.warn('Ignoring order complete event, customer still has active claim record')
            return
          }

          resetApiState()
          dispatch(connectorOrderComplete())
          ApiCache.cleanupCache()
          clearLocalFormState()
        })
        .catch(error => {
          console.warn('Unable to verify order complete', error)
        })
    }

    /**
     * To be removed once brand F/E integrations have removed all interceptor events
     */
    const handleCartEdit = (
      event:
        | CartAddItemEvent
        | CartRemoveItemEvent
        | CartEditItemEvent
        | CartChangeQuantityEvent
        | CartApplySubscriptionEvent
        | CartApplyPromoCodeEvent
        | CartRemovePromoCodeEvent
    ) => {
      // all cart update popups are disabled but still need to invoke
      // the callback until commerce apps remove the integration points
      typeof event?.payload === 'function' && event.payload()
    }

    const handleRequestProductQuote = (event: RequestProductQuoteEvent) => {
      const { request, handler } = event.payload
      if (typeof handler === 'function' && Array.isArray(request) && request.length) {
        requestQuote(request).then(result => {
          handler({ productQuotes: result?.productQuotes ?? [] })
        })
      }
    }

    const handleServiceStatus = (event: ServiceStatusEvent) => {
      const { serviceType, handler } = event.payload
      if (serviceType && typeof handler === 'function') {
        const status = serviceStatus[serviceType]
        status && handler(status)
      }
    }

    const handleAddSyncInsuranceListener = (event: AddSyncInsuranceListenerEvent) => {
      if (typeof event?.payload === 'function') {
        dispatch(addSyncInsuranceListener(event.payload))
      }
    }

    const handleRemoveSyncInsuranceListener = (event: RemoveSyncInsuranceListenerEvent) => {
      if (typeof event?.payload === 'function') {
        dispatch(removeSyncInsuranceListener(event.payload))
      }
    }

    const handleAddInsuranceBenefits = (_event: AddInsuranceBenefitsEvent) => {
      addInsuranceBenefits()
    }

    const handleRemoveInsuranceBenefits = (_event: RemoveInsuranceBenefitsEvent) => {
      removeInsuranceBenefits()
    }

    const handleRequestBenefitPlan = (event: RequestBenefitPlanEvent) => {
      if (typeof event?.payload === 'function') {
        getBenefitPlan().then(response => event.payload(response))
      }
    }

    const handleRequestInsuranceData = (event: RequestInsuranceDataEvent) => {
      if (typeof event?.payload === 'function') {
        getInsuranceData().then(response => event.payload(response))
      }
    }

    const handleLogout = (event: LogoutEvent) => {
      resetApiState()
      resetSliceState({ isLogout: true })
      ApiCache.cleanupCache()
      clearLocalFormState()
      typeof event.payload === 'function' && event.payload()
    }

    return {
      onLogin: event => event.type === 'onLogin' && handleLogin(event),
      onLogout: event => event.type === 'onLogout' && handleLogout(event),
      onSessionError: event => event.type === 'onSessionError' && handleSessionError(event),
      onOrderComplete: event => event.type === 'onOrderComplete' && handleOrderComplete(event),
      onCartAddItem: event => event.type === 'onCartAddItem' && handleCartEdit(event),
      onCartRemoveItem: event => event.type === 'onCartRemoveItem' && handleCartEdit(event),
      onCartEditItem: event => event.type === 'onCartEditItem' && handleCartEdit(event),
      onCartChangeQuantity: event => event.type === 'onCartChangeQuantity' && handleCartEdit(event),
      onCartApplySubscription: event => event.type === 'onCartApplySubscription' && handleCartEdit(event),
      onCartApplyPromoCode: event => event.type === 'onCartApplyPromoCode' && handleCartEdit(event),
      onCartRemovePromoCode: event => event.type === 'onCartRemovePromoCode' && handleCartEdit(event),
      onServiceStatus: event => event.type === 'onServiceStatus' && handleServiceStatus(event),
      onRequestProductQuote: event => event.type === 'onRequestProductQuote' && handleRequestProductQuote(event),
      onAddSyncInsuranceListener: event =>
        event.type === 'onAddSyncInsuranceListener' && handleAddSyncInsuranceListener(event),
      onRemoveSyncInsuranceListener: event =>
        event.type === 'onRemoveSyncInsuranceListener' && handleRemoveSyncInsuranceListener(event),
      onAddInsuranceBenefits: event => event.type === 'onAddInsuranceBenefits' && handleAddInsuranceBenefits(event),
      onRemoveInsuranceBenefits: event =>
        event.type === 'onRemoveInsuranceBenefits' && handleRemoveInsuranceBenefits(event),
      onRequestBenefitPlan: event => event.type === 'onRequestBenefitPlan' && handleRequestBenefitPlan(event),
      onRequestInsuranceData: event => event.type === 'onRequestInsuranceData' && handleRequestInsuranceData(event),
    } as Record<InsuranceEventType, (event: InsuranceEvent) => void>
  }, [
    dispatch,
    getBenefitPlan,
    getInsuranceData,
    requestQuote,
    addInsuranceBenefits,
    removeInsuranceBenefits,
    fetchActiveData,
    serviceStatus,
  ])

  const eventListener = useCallback(
    (event: Event) => {
      if (!isCustomEvent(event)) return

      const insuranceEvent = event?.detail

      // cartbundle event is handled by eligibleProductCheck
      if (insuranceEvent?.type && insuranceEvent.type !== 'onCartBundleChange') {
        if (DEPRECATED_EVENTS.includes(insuranceEvent.type)) {
          console.debug('directbilling received deprecated app-event: ', insuranceEvent)
        } else {
          console.debug('directbilling received app-event: ', insuranceEvent)
        }
        eventHandlers[insuranceEvent.type]?.(insuranceEvent)
      }
    },
    [eventHandlers]
  )

  return eventListener
}

const isCustomEvent = (event: Event): event is CustomEvent<InsuranceEvent> => {
  return event instanceof CustomEvent
}
